import { useEffect } from "react";
import { useLocation } from "wouter";
import { useUser, useOrganization } from "@clerk/clerk-react";
import { useOrgMetadata } from "./use-org-metadata";
import { usePcHomeRoute } from "./use-will-call";
import { SALES_INSIGHTS_ROUTE, isSalesInsightsUser } from "./use-sales-insights";

/**
 * Hook to enforce route protection based on organization type:
 * - Profit centers can only access /pc/* (dashboard, default landing) and
 *   /pc-onboarding/* routes; Sales Insights users only /pc/insights
 * - Customers (and undefined org types) cannot access /pc/* or /pc-onboarding/* routes
 * - Non-PC users who haven't completed onboarding are redirected to /c-onboarding/welcome
 */
export function useOrgRouteProtection() {
  const [location, setLocation] = useLocation();
  const { isSignedIn, isLoaded, user } = useUser();
  const { organization, isLoaded: orgIsLoaded } = useOrganization();
  const { metadata, isLoaded: metadataIsLoaded } = useOrgMetadata();
  const pcHomeRoute = usePcHomeRoute();
  // Includes users whose marker names another PC; the page tells them to switch
  const salesInsightsAccess = isSalesInsightsUser(user, metadata);

  useEffect(() => {
    // Only enforce route protection if user is signed in, has an organization, and metadata is loaded
    if (
      !isLoaded ||
      !isSignedIn ||
      !orgIsLoaded ||
      !organization ||
      !metadataIsLoaded
    ) {
      return;
    }

    const orgType = metadata?.type;
    const isPCOnboardingRoute = location.startsWith("/pc-onboarding");
    const isPCDashboardRoute = location.startsWith("/pc/");
    // Contractor-scoped authoring routes PC staff may use while acting for a
    // contractor (the photo/list flow and the order editing it lands on).
    // Their requests are scoped by the app-level contractor scope provider.
    const isSharedAuthoringRoute =
      location.startsWith("/list") || location.startsWith("/orders/edit/");
    const isContractorOnboardingRoute = location.startsWith("/c-onboarding");
    const isPublicRoute =
      location === "/" ||
      location === "/login" ||
      location.startsWith("/setup") ||
      location.startsWith("/quote-approval");

    // Skip route protection for public routes
    if (isPublicRoute) {
      return;
    }

    // If organization type is "profit-center", they should only access
    // /pc/* and /pc-onboarding/* routes; the dashboard is the default landing
    if (orgType === "profit-center") {
      const isSalesInsightsRoute = location.startsWith(SALES_INSIGHTS_ROUTE);
      // Sales Insights users only use /pc/insights for now; everyone else is
      // kept off it
      if (salesInsightsAccess) {
        if (!isSalesInsightsRoute) {
          setLocation(SALES_INSIGHTS_ROUTE);
        }
      } else if (isSalesInsightsRoute) {
        setLocation(pcHomeRoute);
      } else if (!isPCDashboardRoute && !isPCOnboardingRoute && !isSharedAuthoringRoute) {
        setLocation(pcHomeRoute);
      }
    }
    // If organization type is NOT "profit-center" (customer or undefined), they should NOT access PC routes
    else {
      if (isPCOnboardingRoute || isPCDashboardRoute) {
        setLocation("/orders/new");
      }

      // Enable this once we're ready to send contractors to onboarding
      // Check if non-PC user has completed onboarding
      const onboardedTimestamp = user?.unsafeMetadata?.onboarded as string;
      const createdAt = user?.createdAt || new Date();
      const january1st2026 = new Date("2026-01-01");
      if (
        createdAt > january1st2026 && // User has been created in >=2026
        !onboardedTimestamp && // User has not completed onboarding
        !isContractorOnboardingRoute // User is not already on the onboarding route
      ) {
        // Redirect user to onboarding welcome page
        setLocation("/c-onboarding/welcome");
      }
    }
  }, [
    isLoaded,
    isSignedIn,
    orgIsLoaded,
    organization,
    metadataIsLoaded,
    metadata?.type,
    location,
    setLocation,
    pcHomeRoute,
    salesInsightsAccess,
    user?.unsafeMetadata,
  ]);
}
